import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getAggregateFromServer,
  getDocs,
  limit as queryLimit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  sum,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase';
import type { DownloadRecord } from '@/types';

function toMillis(value: unknown): number | null {
  if (value instanceof Timestamp) return value.toMillis();
  if (typeof value === 'number') return value;
  return null;
}

export function mapDownload(id: string, data: DocumentData): DownloadRecord {
  return {
    id,
    uid: String(data.uid ?? ''),
    sourceUrl: String(data.sourceUrl ?? ''),
    platform: (data.platform ?? 'instagram') as DownloadRecord['platform'],
    outputType: (data.outputType ?? 'video') as DownloadRecord['outputType'],
    status: (data.status ?? 'queued') as DownloadRecord['status'],
    progress: Number(data.progress ?? 0),
    quality: (data.quality as string | null) ?? null,
    fileSize: (data.fileSize as number | null) ?? null,
    durationSec: (data.durationSec as number | null) ?? null,
    downloadUrl: (data.downloadUrl as string | null) ?? null,
    metadata: (data.metadata as DownloadRecord['metadata']) ?? null,
    error: (data.error as string | null) ?? null,
    createdAt: toMillis(data.createdAt) ?? Date.now(),
    completedAt: toMillis(data.completedAt),
    expiresAt: toMillis(data.expiresAt),
  };
}

/** Live queued/processing jobs for the dashboard queue. */
export function subscribeToQueue(
  uid: string,
  callback: (records: DownloadRecord[]) => void
): Unsubscribe {
  const q = query(
    collection(db(), 'downloads'),
    where('uid', '==', uid),
    where('status', 'in', ['queued', 'processing']),
    orderBy('createdAt', 'desc'),
    queryLimit(10)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => mapDownload(d.id, d.data()))),
    (error) => console.error('[queue subscription]', error)
  );
}

/** Live most-recent completed downloads. */
export function subscribeToRecent(
  uid: string,
  callback: (records: DownloadRecord[]) => void,
  count = 6
): Unsubscribe {
  const q = query(
    collection(db(), 'downloads'),
    where('uid', '==', uid),
    where('status', '==', 'completed'),
    orderBy('createdAt', 'desc'),
    queryLimit(count)
  );
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map((d) => mapDownload(d.id, d.data()))),
    (error) => console.error('[recent subscription]', error)
  );
}

export interface HistoryPage {
  records: DownloadRecord[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
}

export async function fetchHistoryPage(
  uid: string,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
  pageSize = 15
): Promise<HistoryPage> {
  const downloads = collection(db(), 'downloads');
  const q = cursor
    ? query(downloads, where('uid', '==', uid), orderBy('createdAt', 'desc'), startAfter(cursor), queryLimit(pageSize))
    : query(downloads, where('uid', '==', uid), orderBy('createdAt', 'desc'), queryLimit(pageSize));
  const snapshot = await getDocs(q);
  const lastDoc = snapshot.docs[snapshot.docs.length - 1];
  return {
    records: snapshot.docs.map((d) => mapDownload(d.id, d.data())),
    nextCursor: snapshot.docs.length === pageSize && lastDoc ? lastDoc : null,
  };
}

export async function deleteDownloadRecord(id: string): Promise<void> {
  await deleteDoc(doc(db(), 'downloads', id));
}

export async function fetchStorageUsedBytes(uid: string): Promise<number> {
  const q = query(
    collection(db(), 'downloads'),
    where('uid', '==', uid),
    where('status', '==', 'completed')
  );
  const aggregate = await getAggregateFromServer(q, { total: sum('fileSize') });
  return aggregate.data().total ?? 0;
}
